import { jsx } from "react/jsx-runtime";
import { useSelector, shallowEqual } from "react-redux";
import { Sections } from "@nxplatform/platform-entities/sport/common-sport.entities";
import { allowedLinks, generateLinkPart, OuterMenuSport } from "@nxplatform/platform-settings/locale-setup.routes";
import { selectOddsBoosterEnabled, selectNewForecastConfigEnabled, isDisabledPromotions } from "@nxplatform/platform-settings/locale-setup.selectors";
import { filterRoutes, mergeRoutes } from "./helpers.js";
import { selectIncomingBetsFeatureEnabled, selectLiveEventsDisabled } from "@nxplatform/platform-settings/platform-settings.selectors";
const useCreateSportRoutes = (pages, Container, additional) => {
  const {
    disabledPromotions,
    disabledLiveEvents,
    isForecastsEnabled,
    isOddsBoosterEnabled,
    incomingBetsEnabled
  } = useSelector((state) => ({
    disabledPromotions: isDisabledPromotions(state),
    disabledLiveEvents: selectLiveEventsDisabled(state),
    isForecastsEnabled: selectNewForecastConfigEnabled(state),
    isOddsBoosterEnabled: selectOddsBoosterEnabled(state),
    incomingBetsEnabled: selectIncomingBetsFeatureEnabled(state)
  }), shallowEqual);
  if (!pages) return [];
  const {
    PageMatch,
    PageLeague,
    pageForecast: PageForecast,
    pageHistory: PageHistory,
    pageFull: PageFull,
    pageSearch: PageSearch,
    pageSearchAll: PageSearchAll,
    LivePage,
    LinePage,
    IncomingBetsPage,
    BoostedOddsPage,
    FavoritesPage,
    CustomSportPage
  } = pages;
  const routes = [
    ...FavoritesPage ? [allowedLinks.favoritesLive, allowedLinks.favoritesLine].map((link) => ({
      path: link,
      element: /* @__PURE__ */ jsx(FavoritesPage, { isLive: link === allowedLinks.favoritesLive })
    })) : [],
    ...CustomSportPage ? [
      {
        path: generateLinkPart(OuterMenuSport.customSportLeagues, [], Sections.line),
        element: /* @__PURE__ */ jsx(PageLeague, { live: false })
      },
      {
        path: generateLinkPart(OuterMenuSport.customSportLeagues, [], Sections.live),
        element: /* @__PURE__ */ jsx(PageLeague, { live: true })
      },
      {
        path: generateLinkPart(OuterMenuSport.customSportLeague, [], Sections.line),
        element: /* @__PURE__ */ jsx(PageLeague, { live: false })
      },
      {
        path: generateLinkPart(OuterMenuSport.customSportLeague, [], Sections.live),
        element: /* @__PURE__ */ jsx(PageLeague, { live: true })
      },
      {
        path: generateLinkPart(OuterMenuSport.customSportSelected, [], Sections.live),
        element: /* @__PURE__ */ jsx(CustomSportPage, { live: true })
      },
      {
        path: generateLinkPart(OuterMenuSport.customSportSelected, [], Sections.line),
        element: /* @__PURE__ */ jsx(CustomSportPage, { live: false })
      },
      {
        path: generateLinkPart(OuterMenuSport.customSport, [], Sections.line),
        element: /* @__PURE__ */ jsx(CustomSportPage, { live: false })
      },
      {
        path: generateLinkPart(OuterMenuSport.customSport, [], Sections.live),
        element: /* @__PURE__ */ jsx(CustomSportPage, { live: true })
      }
    ] : [],
    {
      path: generateLinkPart(OuterMenuSport.sportLine, [], Sections.line),
      element: /* @__PURE__ */ jsx(PageMatch, { live: false, prematch: true })
    },
    ...!disabledLiveEvents ? [{
      path: generateLinkPart(OuterMenuSport.sportLive, [], Sections.live),
      element: /* @__PURE__ */ jsx(PageMatch, { live: true, sectionLive: true })
    }] : [],
    {
      path: generateLinkPart(OuterMenuSport.leagues, [], Sections.line),
      element: /* @__PURE__ */ jsx(PageMatch, { isLeague: true, live: false })
    },
    ...!disabledLiveEvents ? [{
      path: generateLinkPart(OuterMenuSport.leagues, [], Sections.live),
      element: /* @__PURE__ */ jsx(PageMatch, { live: true, isLeague: true })
    }] : [],
    {
      path: generateLinkPart(OuterMenuSport.leaguePage, [], Sections.line),
      element: /* @__PURE__ */ jsx(PageLeague, { live: false })
    },
    ...!disabledLiveEvents ? [{
      path: generateLinkPart(OuterMenuSport.leaguePage, [], Sections.live),
      element: /* @__PURE__ */ jsx(PageLeague, { live: true })
    }] : [],
    {
      path: generateLinkPart(
        OuterMenuSport.outrights,
        [],
        Sections.line
      ),
      element: /* @__PURE__ */ jsx(PageLeague, { live: false })
    },
    ...!disabledLiveEvents ? [{
      path: generateLinkPart(OuterMenuSport.outrights, [], Sections.live),
      element: /* @__PURE__ */ jsx(PageLeague, { live: true })
    }] : [],
    ...PageSearchAll ? [{ path: allowedLinks.searchAll, element: /* @__PURE__ */ jsx(PageSearchAll, {}) }] : [],
    {
      path: generateLinkPart(OuterMenuSport.multiLeaguePage, [], Sections.live),
      element: /* @__PURE__ */ jsx(PageLeague, { live: true })
    },
    {
      path: generateLinkPart(OuterMenuSport.multiLeaguePage, [], Sections.line),
      element: /* @__PURE__ */ jsx(PageLeague, {})
    },
    ...!(disabledPromotions && isForecastsEnabled) ? [
      {
        path: generateLinkPart(OuterMenuSport.forecast, []),
        element: /* @__PURE__ */ jsx(PageForecast, {})
      }
    ] : [],
    {
      path: generateLinkPart(OuterMenuSport.results, []),
      element: /* @__PURE__ */ jsx(PageHistory, {})
    },
    ...!disabledLiveEvents ? [{
      path: generateLinkPart(OuterMenuSport.eventFull, [], Sections.live),
      element: /* @__PURE__ */ jsx(PageFull, {})
    }] : [],
    {
      path: generateLinkPart(OuterMenuSport.eventFull, [], Sections.line),
      element: /* @__PURE__ */ jsx(PageFull, {})
    },
    ...!disabledLiveEvents ? [{
      path: generateLinkPart(OuterMenuSport.eventFullSpecial, [], Sections.live),
      element: /* @__PURE__ */ jsx(PageFull, {})
    }] : [],
    {
      path: generateLinkPart(OuterMenuSport.eventFullSpecial, [], Sections.line),
      element: /* @__PURE__ */ jsx(PageFull, {})
    },
    {
      path: generateLinkPart(OuterMenuSport.search, []),
      element: /* @__PURE__ */ jsx(PageSearch, {})
    },
    ...!disabledLiveEvents ? [{ path: allowedLinks.live, element: /* @__PURE__ */ jsx(LivePage, { live: true, sectionLive: true }) }] : [],
    { path: allowedLinks.line, element: /* @__PURE__ */ jsx(LinePage, { live: false, prematch: true }) },
    ...incomingBetsEnabled ? [{ path: allowedLinks.incomingBets, element: /* @__PURE__ */ jsx(IncomingBetsPage, {}) }] : [],
    ...isOddsBoosterEnabled && BoostedOddsPage ? [{
      path: allowedLinks.boostedOdds,
      element: /* @__PURE__ */ jsx(BoostedOddsPage, {})
    }] : []
  ].filter(filterRoutes);
  return [{
    element: /* @__PURE__ */ jsx(Container, {}),
    children: mergeRoutes(routes, additional)
  }];
};
export {
  useCreateSportRoutes
};
